import React, { createContext, useContext, useMemo, useRef, useState } from "react";

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const sectionsRef = useRef(new Map());
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Saving settings...");

  const api = useMemo(() => {
    return {
      registerSection(id, handlers) {
        if (!id || !handlers) return () => {};
        sectionsRef.current.set(id, handlers);
        return () => {
          sectionsRef.current.delete(id);
        };
      },
      getAllChanges() {
        const changes = [];
        for (const [, handlers] of sectionsRef.current.entries()) {
          if (typeof handlers.getChanges === "function") {
            const next = handlers.getChanges() || [];
            changes.push(...next);
          }
        }
        return changes;
      },
      async saveAll() {
        for (const [, handlers] of sectionsRef.current.entries()) {
          if (typeof handlers.getChanges === "function") {
            const changes = handlers.getChanges() || [];
            if (!changes.length) continue;
          }
          if (typeof handlers.save === "function") {
            await handlers.save();
          }
        }
      },
      async applyToStations(stationIds) {
        const ids = Array.isArray(stationIds) ? stationIds : [];
        if (!ids.length) return;
        for (const targetId of ids) {
          for (const [, handlers] of sectionsRef.current.entries()) {
            if (typeof handlers.applyToStation === "function") {
              await handlers.applyToStation(targetId);
            }
          }
        }
      },
      async applySavedToStations(stationIds) {
        const ids = Array.isArray(stationIds) ? stationIds : [];
        if (!ids.length) return;
        for (const targetId of ids) {
          for (const [, handlers] of sectionsRef.current.entries()) {
            if (typeof handlers.applySavedToStation === "function") {
              await handlers.applySavedToStation(targetId);
            }
          }
        }
      },
      hasSections() {
        return sectionsRef.current.size > 0;
      },
      loading,
      loadingMessage,
      setLoading,
      setLoadingMessage,
    };
  }, [loading, loadingMessage]);

  return <SettingsContext.Provider value={api}>{children}</SettingsContext.Provider>;
}

export function useSettingsRegistry() {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    throw new Error("useSettingsRegistry must be used within SettingsProvider");
  }
  return ctx;
}

export function useSettingsSection(id, handlers) {
  const ctx = useContext(SettingsContext);

  React.useEffect(() => {
    if (!ctx || !id) return;
    return ctx.registerSection(id, handlers);
  }, [ctx, id, handlers]);
}
